import { Box, Chip } from '@mui/material';
import { ServerPaginatedTable } from 'components/table';
import { columnType } from 'components/table/types';
import { generalTranslation } from 'constants/global.constants';
import { DEFAULT_PAGE_SIZE } from 'constants/pagination.constants';
import { useServerPagination, type SortState } from 'hooks/pagination/useServerPagination';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { useCallback, useEffect, useMemo } from 'react';
import { getBeneficiaryDashboardBeneficiaries } from 'services/balances.service';
import {
	clearBeneficiaryDashboardList,
	getBeneficiaryDashboardListLoading,
	getBeneficiaryDashboardRows,
	getBeneficiaryDashboardTotal,
} from 'state/reducers/balance.reducer';
import { selectTranslations } from 'state/reducers/translation.reducers';
import type { IBeneficiaryDashboardDateRange } from 'types/beneficiary-dashboard.types';

type Props = {
	datesRange: IBeneficiaryDashboardDateRange;
};

const formatAvgResponseTime = (hours: number | null | undefined, t: (key: string) => string) => {
	if (hours === null || hours === undefined) return '—';
	if (hours < 1) {
		return `${Math.round(hours * 60)} ${t('page.dashboard.beneficiaryTable.responseTimeMinutes')}`;
	}
	return `${Math.round(hours * 10) / 10} ${t('page.dashboard.beneficiaryTable.responseTimeHours')}`;
};

export const BeneficiaryDashboardTable = ({ datesRange }: Props) => {
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const rows = useAppSelector(getBeneficiaryDashboardRows);
	const total = useAppSelector(getBeneficiaryDashboardTotal);
	const listLoading = useAppSelector(getBeneficiaryDashboardListLoading);

	// useServerPagination always passes sortState; table is not sortable so it is unused.
	const fetchPage = useCallback(
		async (page: number, _sortState: SortState) => {
			await dispatch(
				getBeneficiaryDashboardBeneficiaries(datesRange, {
					offset: page * DEFAULT_PAGE_SIZE,
					limit: DEFAULT_PAGE_SIZE,
				}),
			);
		},
		[datesRange, dispatch],
	);

	const { currentPage, runFetch, handlePageChange } = useServerPagination({
		initialSortBy: 'createdAt',
		fetchPage,
	});

	useEffect(() => {
		dispatch(clearBeneficiaryDashboardList());
		runFetch(0);
		return () => {
			dispatch(clearBeneficiaryDashboardList());
		};
	}, [datesRange, dispatch, runFetch]);

	const columns = useMemo<columnType[]>(
		() => [
			{
				name: 'createdAt',
				title: t('page.customerAccounts.tables.beneficiaries.field.createdAt'),
				type: 'date',
				fieldId: 'createdAt',
			},
			{
				name: 'statusChangeDate',
				title: t('page.dashboard.beneficiaryTable.statusChangeDate'),
				type: 'date',
				fieldId: 'statusChangeDate',
			},
			{
				name: 'companyName',
				title: t('page.dashboard.boSigner.requests.field.hebrewName'),
				type: 'string',
				fieldId: 'companyName',
			},
			{
				name: 'beneficiaryName',
				title: t('page.hyperMonitoring.field.beneficiaryName'),
				type: 'string',
				fieldId: 'beneficiaryName',
			},
			{
				name: 'status',
				title: t('page.customerAccounts.tables.beneficiaries.field.statusHebrew'),
				type: 'custom',
				fieldId: 'status',
				customValueHandle: (value: string) => (
					<Chip size="small" label={generalTranslation[value]} />
				),
			},
			{
				name: 'beneficiaryCountry',
				title: t('page.dashboard.dashboardBalances.table.headers.countryId'),
				type: 'string',
				fieldId: 'beneficiaryCountry',
			},
			{
				name: 'bankAccountCountry',
				title: t('page.dashboard.dashboardBalances.table.headers.bankAccountCountryId'),
				type: 'string',
				fieldId: 'bankAccountCountry',
			},
			{ name: 'currencyCode', title: t('label.currency'), type: 'string', fieldId: 'currencyCode' },
			{
				name: 'boApprovalDate',
				title: t('page.dashboard.beneficiaryTable.boApprovalDate'),
				type: 'date',
				fieldId: 'boApprovalDate',
			},
			{
				name: 'statusChangedByBoName',
				title: t('page.dashboard.beneficiaryTable.statusChangedByBo'),
				type: 'string',
				fieldId: 'statusChangedByBoName',
			},
			{
				name: 'repairRounds',
				title: t('page.dashboard.beneficiaryTable.rounds'),
				type: 'integer',
				fieldId: 'repairRounds',
			},
			{
				name: 'avgResponseHours',
				title: t('page.dashboard.beneficiaryTable.responseTime'),
				type: 'custom',
				fieldId: 'avgResponseHours',
				customValueHandle: (_value: string, row: Record<string, any>) =>
					formatAvgResponseTime(row.avgResponseHours, t),
			},
			{
				name: 'withinSla',
				title: t('page.dashboard.beneficiaryTable.withinSla'),
				type: 'custom',
				fieldId: 'withinSla',
				customValueHandle: (_value: string, row: Record<string, any>) => {
					if (row.withinSla === null || row.withinSla === undefined) return '—';
					return row.withinSla ? '✓' : '✗';
				},
			},
			{
				name: 'createdByName',
				title: t('page.dashboard.beneficiaryTable.createdBy'),
				type: 'string',
				fieldId: 'createdByName',
			},
		],
		[t],
	);

	return (
		<Box sx={{ overflowX: 'auto' }}>
			<ServerPaginatedTable
				data={rows}
				columns={columns}
				dateFormat="dd/MM/yyyy"
				perPage={DEFAULT_PAGE_SIZE}
				noShrink
				serverPagination={{
					total,
					hasMore:
						rows.length === DEFAULT_PAGE_SIZE && (currentPage + 1) * DEFAULT_PAGE_SIZE < total,
					currentPage,
					onPageChange: handlePageChange,
					isLoading: listLoading,
				}}
			/>
		</Box>
	);
};
